import {skipToken} from '@reduxjs/toolkit/query';
import {Alert, ButtonGroup, Button, closeDialog, Flag, TabProps, Tabs} from 'platform/components';
import {Box, Show, Text, VStack} from 'platform/foundation';

import {useCallback, useState} from 'react';

import {isNotNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {
  useGetLastActiveSellingBusinessCaseQuery,
  useGetOfferAlternativesQuery,
  useGetVehicleOfferAlternativesQuery,
} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {DmsDataGrid} from '@dms/shared';

import type {QueryFilterObject} from 'features/eag-grid';

import {SelectVehicleDialogDataGrid} from '../../SelectVehicleDialogDataGrid';
import {getOfferedElsewhere} from './getOfferedElsewhere';
import {isStaleRevisionError} from './handleOfferAlternativeError';
import {type OfferAlternativeSource} from './OfferAlternative';

const t = (key: string, fallback: string) => i18n.t(key, {defaultValue: fallback});

const TAB_IDS = {
  CONFIGURATION: 'configuration',
  STOCK: 'stock',
} as const;

// The nil UUID stands for "no Business Case" in the businessCaseIdOrUnassigned filter.
const NO_BUSINESS_CASE_ID = '00000000-0000-0000-0000-000000000000';

interface AlternativeSourceDialogProps {
  businessCaseId: string;
  dialogId: string;
  /** Resolves when the source was saved; the dialog closes then. */
  onSubmit: (source: OfferAlternativeSource) => Promise<unknown>;
}

/** Picks the source of an alternative: a saved configuration or a stock vehicle. */
export function AlternativeSourceDialog(props: AlternativeSourceDialogProps) {
  const [activeTabId, setActiveTabId] = useState<string>(TAB_IDS.CONFIGURATION);
  const [configurationId, setConfigurationId] = useState<string>();
  const [vehicleId, setVehicleId] = useState<string>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const configurationSource = configurationId ? {configurationId} : null;
  const vehicleSource = vehicleId ? {vehicleId} : null;
  const isStockTab = activeTabId === TAB_IDS.STOCK;
  const source: OfferAlternativeSource | null = isStockTab ? vehicleSource : configurationSource;

  // A source may be offered once (withdrawn ones are not listed, so they can return).
  const {data: offerAlternatives} = useGetOfferAlternativesQuery({
    businessCaseId: props.businessCaseId,
  });
  const takenSourceIds = (offerAlternatives?.alternatives ?? []).flatMap((alternative) =>
    [alternative.configurationId, alternative.vehicleId].filter(isNotNil)
  );
  const selectedSourceId = isStockTab ? vehicleId : configurationId;
  const isAlreadyIncluded = isNotNil(selectedSourceId) && takenSourceIds.includes(selectedSourceId);

  // A stock vehicle another open Business Case offers is refused on save; say so on selection.
  // `currentData`, so a previous row's answer never shows for the next one.
  const stockVehicleArg = isStockTab && isNotNil(vehicleId) ? {vehicleId} : skipToken;
  const lastSellingQuery = useGetLastActiveSellingBusinessCaseQuery(stockVehicleArg);
  const vehicleOfferAlternativesQuery = useGetVehicleOfferAlternativesQuery(stockVehicleArg);
  const offeredElsewhere = isStockTab
    ? getOfferedElsewhere(
        props.businessCaseId,
        lastSellingQuery.currentData,
        vehicleOfferAlternativesQuery.currentData?.items ?? []
      )
    : [];
  const isCheckingStockVehicle =
    lastSellingQuery.isFetching || vehicleOfferAlternativesQuery.isFetching;
  const offeredElsewhereCodes = offeredElsewhere
    .map((offering) => offering.businessCasePublicId)
    .filter(isNotNil)
    .join(', ');

  const submit = () => {
    if (!source) {
      return;
    }
    setIsSubmitting(true);
    props
      .onSubmit(source)
      .then(() => closeDialog(props.dialogId))
      // Already reported; a stale revision closes so reopening reads the fresh alternative.
      .catch((error: unknown) => {
        if (isStaleRevisionError(error)) {
          closeDialog(props.dialogId);
        }
      })
      .finally(() => setIsSubmitting(false));
  };

  // This Business Case's own unordered configurations plus unassigned ones, minus any that are
  // already a live alternative anywhere. Stable identity: the grid re-queries when it changes.
  const configurationQueryModifier = useCallback(
    (filter: QueryFilterObject) => ({
      ...filter,
      // Saved views of this grid may still carry businessCaseUnassigned (the former picker filter);
      // it would hide this Business Case's own configurations, so it is never sent.
      businessCaseUnassigned: undefined,
      businessCaseIdOrUnassigned: [props.businessCaseId, NO_BUSINESS_CASE_ID],
      vehicleUnassigned: true,
      offerAlternativeUnassigned: true,
    }),
    [props.businessCaseId]
  );

  const tabs: TabProps[] = [
    {
      id: TAB_IDS.CONFIGURATION,
      title: t('entity.offerAlternative.source.configuration', 'Saved configuration'),
      content: (
        <VStack spacing={3}>
          <Text color="secondary" size="small">
            {t(
              'entity.offerAlternative.source.configurationHint',
              'A saved configuration has no VIN. The vehicle is ordered only after the customer chooses it.'
            )}
          </Text>
          <Box height="55vh">
            <DmsDataGrid
              gridCode="business-case-add-new-vehicle-configuration"
              presetInstanceId={`offer-alternative-configuration-${props.businessCaseId}`}
              queryModifier={configurationQueryModifier}
              onRowSelectionChange={(rows) => setConfigurationId(rows[0]?.id)}
            />
          </Box>
        </VStack>
      ),
    },
    {
      id: TAB_IDS.STOCK,
      title: t('entity.offerAlternative.source.stock', 'Stock vehicle'),
      content: (
        <SelectVehicleDialogDataGrid onRowSelectionChange={(rows) => setVehicleId(rows[0]?.id)} />
      ),
    },
  ];

  return (
    <VStack spacing={4}>
      <Tabs tabs={tabs} activeTabId={activeTabId} onChange={setActiveTabId} variant="condensed" />
      <Show when={offeredElsewhere.length > 0}>
        <Alert
          variant="warning"
          title={t(
            'entity.offerAlternative.source.offeredElsewhere',
            'This vehicle is already offered in another business case. Choose another vehicle.'
          )}
          message={isNotNilOrEmpty(offeredElsewhereCodes) ? offeredElsewhereCodes : undefined}
          data-testid="offerAlternativeSourceDialog-offeredElsewhere"
        />
      </Show>
      <ButtonGroup align="right">
        <Show when={isAlreadyIncluded}>
          <Flag
            size="small"
            colorScheme="neutral"
            label={t('entity.offerAlternative.source.alreadyIncluded', 'Already included')}
            data-testid="offerAlternativeSourceDialog-alreadyIncluded"
          />
        </Show>
        <Button
          variant="secondary"
          title={i18n.t('general.actions.cancel')}
          onClick={() => closeDialog(props.dialogId)}
        />
        <Button
          variant="primary"
          title={i18n.t('general.labels.select')}
          isDisabled={
            !source ||
            isAlreadyIncluded ||
            (isStockTab && (isCheckingStockVehicle || offeredElsewhere.length > 0))
          }
          isLoading={isSubmitting}
          onClick={submit}
          data-testid="offerAlternativeSourceDialog-submit"
        />
      </ButtonGroup>
    </VStack>
  );
}
